import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Menu, Moon, Sun, X } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { scrollToId } from "@/lib/smooth";
import { ARTIST, PORTFOLIO_URL, siteImg } from "@/lib/constants";
import { PAGES } from "@/lib/site";

const LINKS = [
  { id: "home", label: "Home", testId: "nav-link-home" },
  { id: "artist", label: "The Artist", testId: "nav-link-artist" },
  { id: "gallery", label: "Gallery", testId: "nav-link-gallery" },
  { id: "world", label: "World of Mithila", testId: "nav-link-world" },
  { id: "recognition", label: "Recognition", testId: "nav-link-recognition" },
  { id: "exhibitions", label: "Exhibitions", testId: "nav-link-exhibitions" },
  { id: "contact", label: "Contact", testId: "nav-link-contact" },
];

export const sectionPath = (id) => PAGES[id]?.path || "/";

// Section links point at each chapter's own page, so they can be crawled and
// indexed; on Home they keep scrolling to the chapter instead of navigating.
export const useSectionLink = (onFollow) => {
  const location = useLocation();
  return (id) => (e) => {
    onFollow?.();
    if (location.pathname !== "/") return;
    e.preventDefault();
    scrollToId(id);
  };
};

export const Navbar = () => {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = useSectionLink(() => setOpen(false));

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-border bg-background/85 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" onClick={go("home")} data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src={siteImg("/assets/logo.png")}
              alt={`${ARTIST.name} logo`}
              className="h-11 w-11 rounded-full object-cover ring-1 ring-foreground/20 sm:h-12 sm:w-12"
            />
            <span className="leading-tight">
              <span className="block font-serif text-base font-semibold tracking-tight sm:text-lg">
                {ARTIST.name}
              </span>
              <span className="eyebrow block text-[9px] text-muted-foreground">Mithila · Madhubani</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-5 lg:flex">
            {LINKS.map((l) => (
              <Link
                key={l.id}
                to={sectionPath(l.id)}
                onClick={go(l.id)}
                data-testid={l.testId}
                className="group flex items-center gap-1.5 text-[12.5px] font-medium tracking-wide text-muted-foreground transition-colors hover:text-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-accent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={PORTFOLIO_URL}
              data-testid="nav-portfolio-download"
              aria-label="Download portfolio PDF"
              title="Download portfolio (PDF)"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border transition-colors hover:border-accent hover:text-accent"
            >
              <Download size={16} />
            </a>
            <button
              onClick={toggle}
              data-testid="theme-toggle-button"
              aria-label="Toggle dark and light theme"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border transition-colors hover:border-accent hover:text-accent"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-menu-button"
              aria-label="Open menu"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border lg:hidden"
            >
              <Menu size={16} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] flex flex-col bg-background px-8 py-6"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between">
              <img src={siteImg("/assets/logo.png")} alt="" className="h-12 w-12 rounded-full object-cover ring-1 ring-foreground/20" />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border"
              >
                <X size={16} />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5 }}
                >
                  <Link
                    to={sectionPath(l.id)}
                    onClick={go(l.id)}
                    className="flex items-center gap-4 font-serif text-3xl font-medium tracking-tight"
                  >
                    <span className="font-mono text-xs text-accent">0{i + 1}</span>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.a
              href={PORTFOLIO_URL}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              data-testid="nav-mobile-portfolio-download"
              className="mt-10 flex items-center gap-3 self-start rounded-full border border-foreground/30 px-6 py-3 text-[13px] font-semibold tracking-wide transition-colors hover:border-accent hover:text-accent"
            >
              <Download size={15} /> Download Portfolio (PDF)
            </motion.a>
            <p className="eyebrow mt-auto text-muted-foreground">{ARTIST.location}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
